import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Button,
  SwipeableDrawer,
  Tooltip,
  useTheme,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  QueryStats as ResearchIcon,
  AccountBalance as PortfolioIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  CalendarToday as CalendarIcon,
  EmojiEvents as SuperInvestorsIcon,
  Calculate as ProjectionsIcon,
  Close as CloseIcon,
  RocketLaunch as RocketLaunchIcon,
  CompareArrows as CompareArrowsIcon,
  Psychology as InsightAiIcon,
  FilterListRounded as ScreenerIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { useSidebar, SIDEBAR_WIDTH_EXPANDED } from "../../../contexts/SidebarContext";
import { isNative } from "../../../lib/platform";
import { hapticSelection } from "../../../lib/haptics";
import { markNavStart } from "../../../lib/navPerf";
import { useMembership } from "../../../contexts/MembershipContext";
import UserProfileButton from "../../auth/UserProfileButton";

interface SidebarProps {
  isMobileMode?: boolean;
}

const Sidebar = ({ isMobileMode = false }: SidebarProps) => {
  const { isCollapsed, toggleCollapsed, sidebarWidth, isMobileDrawerOpen, closeMobileDrawer, openMobileDrawer } = useSidebar();
  const { isMembershipActive } = useMembership();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // Listen for tour events to open/close mobile drawer
  useEffect(() => {
    const handleTourDrawerEvent = (event: CustomEvent<{ action: 'open' | 'close' }>) => {
      if (event.detail.action === 'open') {
        openMobileDrawer();
      } else {
        closeMobileDrawer();
      }
    };

    window.addEventListener('tour-mobile-drawer', handleTourDrawerEvent as EventListener);
    return () => {
      window.removeEventListener('tour-mobile-drawer', handleTourDrawerEvent as EventListener);
    };
  }, [openMobileDrawer, closeMobileDrawer]);

  // In mobile mode, always show expanded style
  const effectiveIsCollapsed = isMobileMode ? false : isCollapsed;
  const effectiveSidebarWidth = isMobileMode ? SIDEBAR_WIDTH_EXPANDED : sidebarWidth;

  const disabledForFreeTier = !isMembershipActive;

  const topMenuItems = [
    {
      text: "Dashboard",
      icon: <DashboardIcon />,
      path: "/dashboard",
    },
    {
      text: "Research",
      icon: <ResearchIcon />,
      path: "/charts",
    },
    {
      // Non-breaking space in the label: the selected state's bolder weight
      // otherwise pushes it past the rail width and wraps it to two lines.
      text: "Insight AI",
      icon: <InsightAiIcon />,
      path: "/chat",
    },
    {
      text: "Screener",
      icon: <ScreenerIcon />,
      path: "/screener",
    },
    {
      text: "Earnings",
      icon: <CalendarIcon />,
      path: "/earnings",
    },
    {
      text: "Compare",
      icon: <CompareArrowsIcon />,
      path: "/compare",
    },
    {
      text: "Investors",
      icon: <SuperInvestorsIcon />,
      path: "/super-investors",
    },
    {
      text: "Projections",
      icon: <ProjectionsIcon />,
      path: "/projections",
      disabled: disabledForFreeTier,
      tooltip: "Available with Insight Pro",
    },
  ];

  const bottomMenuItems = [
    // Upgrade entry only shown for non-members. The native app sells no
    // free trial (weekly is the low-commitment entry), so no trial wording.
    ...(!isMembershipActive
      ? [
          {
            text: isNative() ? "Upgrade" : "Start Free Trial",
            icon: <RocketLaunchIcon />,
            path: "/start-free-trial",
            isTrialButton: true,
          },
        ]
      : []),
  ];

  const handleItemClick = (path: string) => {
    hapticSelection();
    markNavStart("sidebar");
    navigate(path);
    // Close mobile drawer after navigation
    if (isMobileMode) {
      closeMobileDrawer();
    }
  };

  const sidebarContent = (
    <Box
      data-tour="sidebar"
      sx={{
        width: effectiveSidebarWidth,
        height: "100vh",
        position: isMobileMode ? "relative" : "fixed",
        left: isMobileMode ? undefined : 0,
        top: isMobileMode ? undefined : 0,
        background: (theme) =>
          theme.palette.mode === "dark"
            ? "#1a1a1a" // Grey flat UI dark primary - matches flatUIColors.background
            : "#ffffff", // Pure white - matches flatUIColors.background
        borderRight: isMobileMode
          ? "none"
          : (theme) =>
              theme.palette.mode === "dark"
                ? "1px solid rgba(255,255,255,0.1)"
                : "1px solid rgba(0,0,0,0.1)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        boxShadow: "none", // Flat UI - no shadows
        transition: isMobileMode ? "none" : "width 0.3s ease",
      }}
    >
      {/* Logo Section — hidden in the native app: the drawer slides under
          the status bar and the top bar already carries the brand */}
      {!isNative() && (
      <>
      <Box
        onClick={() => {
          navigate("/dashboard");
          if (isMobileMode) closeMobileDrawer();
        }}
        sx={{
          cursor: "pointer",
          height: 64, // Match topbar height from ViewTickerV2
          display: "flex",
          alignItems: "center",
          justifyContent: effectiveIsCollapsed ? "center" : "flex-start",
          px: effectiveIsCollapsed ? 2 : 3,
          py: 2,
          gap: effectiveIsCollapsed ? 0 : 2,
          transition: "all 0.3s ease",
          "&:hover": {
            "& .logo-icon": {
              transform: "rotate(5deg) scale(1.05)",
            },
          },
        }}
      >
        {effectiveIsCollapsed ? (
          <img
            className="logo-icon"
            src="/insightShortLogoPrimary.svg"
            alt="Insight Analytics Logo"
            style={{
              width: 32,
              height: 32,
              transition: "all 0.3s ease",
            }}
          />
        ) : (
          <img
            className="logo-icon"
            src="/insightLongLogoPrimaryWhiteText.svg"
            alt="Insight Analytics Logo"
            style={{
              height: 40,
              width: "auto",
              maxWidth: "180px",
              transition: "all 0.3s ease",
            }}
          />
        )}
      </Box>

      {/* Logo Divider */}
      <Divider
        sx={{
          mx: 2,
          mb: 1,
          borderColor: (theme) =>
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.08)"
              : "rgba(0, 0, 0, 0.08)",
        }}
      />
      </>
      )}

      {/* Navigation Section */}
      <Box
        sx={{
          flex: 1,
          px: 1,
          // Native: the drawer extends under the status bar, so the first
          // item needs notch clearance (env() is 0 in ordinary browsers)
          pt: "calc(env(safe-area-inset-top, 0px) + 16px)",
          pb: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Top Menu Items */}
        <List sx={{ padding: 0, flexGrow: 1 }}>
          {topMenuItems.map((item) => {
            const isDisabled = Boolean("disabled" in item && item.disabled);
            const tooltipText = "tooltip" in item ? (item as any).tooltip : "";
            const isSelected =
              location.pathname === item.path ||
              location.pathname.startsWith(item.path + "/");

            const listItemContent = (
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: effectiveIsCollapsed ? "column" : "row",
                  alignItems: "center",
                  gap: effectiveIsCollapsed ? 0.25 : 0,
                  ...(isDisabled && {
                    opacity: 0.4,
                    pointerEvents: "none",
                  }),
                }}
              >
                <ListItemButton
                  selected={isSelected}
                  onClick={() => !isDisabled && handleItemClick(item.path)}
                  disabled={isDisabled}
                  sx={{
                    borderRadius: 2,
                    mx: 0.5,
                    py: effectiveIsCollapsed ? 1 : 1.5,
                    transition: "all 0.3s ease",
                    justifyContent: effectiveIsCollapsed ? "center" : "flex-start",
                    minHeight: effectiveIsCollapsed ? 40 : 48,
                    width: effectiveIsCollapsed ? 40 : "auto",
                    ...(effectiveIsCollapsed && {
                      "& .MuiListItemIcon-root svg": {
                        fontSize: "1.25rem",
                      },
                    }),
                    "&:hover": {
                      transform: effectiveIsCollapsed ? "none" : "translateX(4px)",
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.08)"
                          : "rgba(0,0,0,0.04)",
                    },
                    "&.Mui-selected": {
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.08)"
                          : "rgba(0, 0, 0, 0.06)",
                      color: (theme) =>
                        theme.palette.mode === "dark" ? "#ffffff" : "#1d1d1f",
                      "&:hover": {
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.12)"
                            : "rgba(0, 0, 0, 0.08)",
                      },
                      "& .MuiListItemIcon-root": {
                        color: (theme) =>
                          theme.palette.mode === "dark" ? "#ffffff" : "#1d1d1f",
                      },
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: effectiveIsCollapsed ? "auto" : 40,
                      justifyContent: "center",
                      transition: "all 0.3s ease",
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {!effectiveIsCollapsed && (
                    <ListItemText
                      primary={item.text}
                      slotProps={{
                        primary: {
                          style: {
                            fontWeight: isSelected ? 600 : 500,
                            fontSize: "0.95rem",
                            whiteSpace: "nowrap",
                          },
                        },
                      }}
                      sx={{
                        ml: 1,
                      }}
                    />
                  )}
                </ListItemButton>
                {effectiveIsCollapsed && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.6rem",
                      fontWeight: isSelected ? 600 : 500,
                      textAlign: "center",
                      lineHeight: 1,
                      color: isSelected
                        ? theme.palette.text.primary
                        : "inherit",
                      transition: "all 0.3s ease",
                      mt: -0.25,
                      px: 0.5,
                      py: 0.25,
                    }}
                  >
                    {item.text}
                  </Typography>
                )}
              </Box>
            );

            return (
              <ListItem key={item.text} disablePadding sx={{ mb: 0.25 }}>
                {isDisabled && tooltipText ? (
                  <Tooltip title={tooltipText} placement="right" arrow>
                    <span style={{ width: "100%" }}>{listItemContent}</span>
                  </Tooltip>
                ) : (
                  listItemContent
                )}
              </ListItem>
            );
          })}
        </List>

        {/* Bottom Menu Items */}
        <List sx={{ padding: 0, mb: 1 }}>
          {bottomMenuItems
            .map((item) => {
              const isDisabled = Boolean("disabled" in item && item.disabled);
              const isTrialButton = "isTrialButton" in item && item.isTrialButton;
              const tooltipText = "tooltip" in item ? (item as any).tooltip : "";
              const isSelected =
                location.pathname === item.path ||
                location.pathname.startsWith(item.path + "/");

              const bottomItemContent = (
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: effectiveIsCollapsed ? "column" : "row",
                    alignItems: "center",
                    gap: effectiveIsCollapsed ? 0.25 : 0,
                    ...(isDisabled && {
                      opacity: 0.4,
                      pointerEvents: "none",
                    }),
                  }}
                >
                  <ListItemButton
                    selected={!isTrialButton && isSelected}
                    onClick={() => !isDisabled && handleItemClick(item.path)}
                    disabled={isDisabled}
                    sx={{
                      borderRadius: 2,
                      mx: 0.5,
                      py: effectiveIsCollapsed ? 1 : 1.5,
                      transition: "all 0.3s ease",
                      justifyContent: effectiveIsCollapsed
                        ? "center"
                        : "flex-start",
                      minHeight: effectiveIsCollapsed ? 40 : 48,
                      width: effectiveIsCollapsed ? 40 : "auto",
                      ...(effectiveIsCollapsed && {
                        "& .MuiListItemIcon-root svg": {
                          fontSize: "1.25rem",
                        },
                      }),
                      ...(isTrialButton && {
                        "& .MuiListItemIcon-root": {
                          color: theme.palette.primary.main,
                        },
                        ...(isSelected && {
                          border: `1px solid ${theme.palette.primary.main}`,
                          borderRadius: 2.5,
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "rgba(34, 197, 94, 0.12)"
                              : "rgba(34, 197, 94, 0.08)",
                        }),
                      }),
                      "&:hover": {
                        transform: effectiveIsCollapsed
                          ? "none"
                          : "translateX(4px)",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.08)"
                            : "rgba(0,0,0,0.04)",
                        ...(isTrialButton && {
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "rgba(34, 197, 94, 0.18)"
                              : "rgba(34, 197, 94, 0.12)",
                        }),
                      },
                      "&.Mui-selected": {
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.08)"
                            : "rgba(0, 0, 0, 0.06)",
                        color: theme.palette.text.primary,
                        "&:hover": {
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "rgba(255, 255, 255, 0.12)"
                              : "rgba(0, 0, 0, 0.08)",
                        },
                        "& .MuiListItemIcon-root": {
                          color: theme.palette.text.primary,
                        },
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: effectiveIsCollapsed ? "auto" : 40,
                        justifyContent: "center",
                        transition: "all 0.3s ease",
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    {!effectiveIsCollapsed && (
                      <ListItemText
                        primary={item.text}
                        slotProps={{
                          primary: {
                            style: {
                              fontWeight: isSelected ? 600 : 500,
                              fontSize: isTrialButton ? "0.8125rem" : "0.95rem",
                              whiteSpace: "nowrap",
                              ...(isTrialButton && { color: theme.palette.primary.main }),
                            },
                          },
                        }}
                        sx={{ ml: 1 }}
                      />
                    )}
                  </ListItemButton>
                  {effectiveIsCollapsed && (
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "0.6rem",
                        fontWeight: isSelected ? 600 : 500,
                        textAlign: "center",
                        lineHeight: 1,
                        color: isTrialButton
                          ? theme.palette.primary.main
                          : isSelected
                            ? theme.palette.text.primary
                            : "inherit",
                        transition: "all 0.3s ease",
                        mt: -0.25,
                        px: 0.5,
                        py: 0.25,
                      }}
                    >
                      {item.text}
                    </Typography>
                  )}
                </Box>
              );

              return (
                <ListItem key={item.text} disablePadding sx={{ mb: isTrialButton ? 1 : 0 }}>
                  {isDisabled && tooltipText ? (
                    <Tooltip title={tooltipText} placement="right" arrow>
                      <span style={{ width: "100%" }}>
                        {bottomItemContent}
                      </span>
                    </Tooltip>
                  ) : (
                    bottomItemContent
                  )}
                </ListItem>
              );
            })}
        </List>
      </Box>

      {/* Bottom Controls */}
      <Box sx={{ p: 2, pt: 0 }}>
        <Divider sx={{ mb: 2, opacity: 0.5 }} />

        {/* User Button */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mb: isMobileMode ? 0 : 2,
          }}
        >
          <UserProfileButton />
        </Box>

        {/* Collapse Toggle - hide on mobile */}
        {!isMobileMode && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Button
              color="inherit"
              onClick={toggleCollapsed}
              sx={{
                width: effectiveIsCollapsed ? 36 : "100%",
                height: 36,
                minWidth: 36,
                minHeight: 36,
                justifyContent: "center",
                p: 0,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 500,
                color: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.7)"
                    : "rgba(0,0,0,0.6)",
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(0,0,0,0.04)",
                },
              }}
            >
              {effectiveIsCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );

  // In mobile mode, wrap sidebar content in a SwipeableDrawer (swipe left to
  // dismiss). On NATIVE the left screen edge belongs to the back gesture
  // (useEdgeSwipeBack — iOS convention), so swipe-to-open is disabled there
  // and the menu opens via the burger / More tab; mobile WEB keeps
  // edge-swipe-to-open.
  if (isMobileMode) {
    return (
      <SwipeableDrawer
        anchor="left"
        open={isMobileDrawerOpen}
        onClose={closeMobileDrawer}
        onOpen={openMobileDrawer}
        swipeAreaWidth={20}
        disableSwipeToOpen={isNative()}
        disableBackdropTransition={isNative()}
        disableDiscovery={isNative()}
        sx={{
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: SIDEBAR_WIDTH_EXPANDED,
            background: (theme) =>
              theme.palette.mode === "dark" ? "#1a1a1a" : "#ffffff",
            borderRight: "none",
          },
        }}
      >
        {sidebarContent}
      </SwipeableDrawer>
    );
  }

  // Desktop mode - render fixed sidebar
  return sidebarContent;
};

export default Sidebar;
